// ProjectModal.js - ČISTÁ VERZE bez ESLint chyb

import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import TechnicianSelect from './TechnicianSelect';
import { useTranslation } from 'react-i18next';

const ProjectModal = ({ isOpen, onClose, project, onSave, onDelete, technicians = [] }) => {
  const { t } = useTranslation();
  
  const initialProjectData = useMemo(() => ({
    contractNumber: '',
    name: '',
    location: '',
    status: 'Active',
    assignedTechnicians: [],
    description: '',
    startDate: new Date().toISOString().split('T')[0],
  }), []);

  const [projectData, setProjectData] = useState(initialProjectData);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (project) {
      // Remove files, photos and other large fields from the projectData
      const { files, photos, workLogs, signature, ...projectFields } = project;
      
      // Handle both _id (MongoDB) and id (D1) formats for backward compatibility
      const technicianIds = project.assignedTechnicians?.map(tech => {
        if (typeof tech === 'object') {
          return tech.id || tech._id;
        } else {
          return tech;
        }
      }) || [];
      
      // ✅ FIX: Ensure all values are strings to prevent React warning
      setProjectData({
        contractNumber: projectFields.contractNumber || projectFields.contract_number || '',
        name: projectFields.name || '',
        location: projectFields.location || '',
        status: projectFields.status || 'Active',
        description: projectFields.description || '',
        assignedTechnicians: technicianIds,
        startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : 
                   project.start_date ? new Date(project.start_date).toISOString().split('T')[0] : '',
      });
    } else {
      setProjectData(initialProjectData);
    }
    
    setErrors({});
  }, [project, initialProjectData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(projectData);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!projectData.contractNumber) newErrors.contractNumber = t('projects.validation.contractNumberRequired', 'Contract number is required');
    if (!projectData.name) newErrors.name = t('projects.validation.nameRequired', 'Project name is required');
    if (!projectData.location) newErrors.location = t('projects.validation.locationRequired', 'Location is required');
    if (!projectData.startDate) newErrors.startDate = t('projects.validation.startDateRequired', 'Start date is required');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDelete = () => {
    if (window.confirm(t('common.messages.confirmDelete'))) {
      const projectId = project.id || project._id;
      onDelete(projectId);
    }
  };

  if (!isOpen) return null;

  const projectStatusOptions = [
    { value: 'Active', label: t('common.status.active') },
    { value: 'Pending', label: t('common.status.pending') },
    { value: 'Completed', label: t('common.status.completed') },
    { value: 'OnHold', label: t('common.status.onHold') },
    { value: 'Cancelled', label: t('common.status.cancelled') }
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:max-w-lg shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {project ? t('projects.edit') : t('projects.new')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Contract Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('projects.contractNumber')}
            </label>
            <input
              type="text"
              required
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                errors.contractNumber ? 'border-red-500' : ''
              }`}
              value={projectData.contractNumber}
              onChange={(e) => setProjectData({ ...projectData, contractNumber: e.target.value })}
            />
            {errors.contractNumber && (
              <p className="mt-1 text-sm text-red-500">{errors.contractNumber}</p>
            )}
          </div>

          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('projects.name')}
            </label>
            <input
              type="text"
              required
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                errors.name ? 'border-red-500' : ''
              }`}
              value={projectData.name}
              onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('projects.location')}
            </label>
            <input
              type="text"
              required
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                errors.location ? 'border-red-500' : ''
              }`}
              value={projectData.location}
              onChange={(e) => setProjectData({ ...projectData, location: e.target.value })}
            />
            {errors.location && (
              <p className="mt-1 text-sm text-red-500">{errors.location}</p>
            )}
          </div>

          {/* Status and Start Date Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('projects.status')}
              </label>
              <select
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                value={projectData.status}
                onChange={(e) => setProjectData({ ...projectData, status: e.target.value })}
              >
                {projectStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('projects.startDate')}
              </label>
              <input
                type="date"
                required
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                  errors.startDate ? 'border-red-500' : ''
                }`}
                value={projectData.startDate}
                onChange={(e) => setProjectData({ ...projectData, startDate: e.target.value })}
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-500">{errors.startDate}</p>
              )}
            </div>
          </div>

          {/* Technician Selection */}
          <div>
            <TechnicianSelect
              technicians={technicians}
              selectedTechnicians={projectData.assignedTechnicians}
              onChange={(selected) => setProjectData({ ...projectData, assignedTechnicians: selected })}
            />
          </div>

          {/* Project Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('projects.description')}</label>
            <textarea
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              value={projectData.description}
              onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-between space-x-3 mt-6">
            {/* Delete button - only show when editing an existing project */}
            {project && (
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                {t('projects.deleteProject')}
              </button>
            )}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {t('common.actions.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                {project ? t('common.actions.update') : t('common.actions.create')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectModal;